/**
 * Every query key in the app, in one place.
 *
 * They used to live next to the queries that read them, one factory per hook
 * module, which is fine right up until a *write* has to reach a cache some other
 * page owns — and most writes do. `lib/cacheSync.ts` is that map, and it needs
 * to name every root at once; built out of the hook modules it would have imported
 * half of them back into itself, and every one of those hooks imports it. So the
 * key space moved here, to a module that imports nothing.
 *
 * The hook modules re-export their own factories, so a query still reads its key
 * from the module it belongs to and nothing outside had to change.
 *
 * The shape is the same throughout: `all` is the prefix every other member
 * extends, because `invalidateQueries` matches by prefix — invalidating `all`
 * reaches every entry under it, whatever arguments it was keyed with. A factory
 * that takes an argument therefore also needs an argument-free prefix wherever a
 * write knows the resource but not the scope (`adminKeys.peopleAll`,
 * `attendanceKeys.overviews`, `gradebookKeys.submissions`).
 *
 * "Imports nothing" means nothing at *runtime*. The one `import type` below is
 * erased by the compiler, so it cannot re-form the cycle this module exists to
 * break — and it keeps a key factory's argument as narrow as the endpoint it
 * addresses.
 */

import type { ChangeRequestListStatus } from '@/hooks/useSubmissionsQueries'

// ============ The catalogue: one endpoint, one key ============

/**
 * What the school teaches, and which sections of it run in which term.
 *
 * These two are the exception to the "a root per page" shape the rest of this
 * module has, and they are here because that shape was a mistake for them.
 * `GET /api/term-courses` had grown three roots — the admin table's, the
 * gradebook scope's, the Settings dropdown's — and `GET /api/courses` two.
 * Nothing distinguished the copies: same URL, same response, kept apart only by
 * which page had asked first. So a session paid for the list again on each
 * crossing, and `cacheSync` had to remember to name every root on every write
 * that touched one — which `departments()` and `gradeLevels()` did not, leaving
 * the gradebook's copy of the catalogue answering from before the edit.
 *
 * One key each. The namespaces below still name them, so a query reads its key
 * from the module it belongs to and nothing at a call site had to change.
 *
 * They deliberately sit outside `admin`/`gradebook`, which means a write that
 * invalidates one of those prefixes wholesale no longer reaches the catalogue.
 * That is the right answer: a mark does not change which sections exist.
 */
export const catalogKeys = {
  courses: () => ['catalog', 'courses'] as const,
  termCourses: () => ['catalog', 'termCourses'] as const,
}

// ============ Admin: the school's records ============

export const adminKeys = {
  all: ['admin'] as const,
  // Academic Years
  academicYears: () => [...adminKeys.all, 'academicYears'] as const,
  // Shared. The catalogue is one entry app-wide — see `catalogKeys`.
  courses: catalogKeys.courses,
  terms: () => [...adminKeys.all, 'terms'] as const,
  termCourses: catalogKeys.termCourses,
  students: () => [...adminKeys.all, 'students'] as const,
  enrollments: () => [...adminKeys.all, 'enrollments'] as const,
  // Teachers
  teachers: () => [...adminKeys.all, 'teachers'] as const,
  departments: () => [...adminKeys.all, 'departments'] as const,
  // People — the whole-school directory, as opposed to the per-type lists above.
  people: (filters?: { role?: string; search?: string }) =>
    [...adminKeys.all, 'people', filters ?? {}] as const,
  /**
   * Every directory page, whatever it is filtered to.
   *
   * `people()` happens to reach them too, but only by a rule that is easy to
   * read past: query-core matches an object inside a key *partially*, so the
   * `{}` that `people()` puts there is a match for `{ role: 'coordinator' }`
   * rather than a different key. Invalidating through this one says "every
   * directory page" outright instead of resting on that, which is why the three
   * person writes spelled it `[...adminKeys.all, 'people']` by hand — in three
   * copies, with no name on what they were doing.
   */
  peopleAll: () => [...adminKeys.all, 'people'] as const,
  // Parents
  parents: () => [...adminKeys.all, 'parents'] as const,
  parentStudentLinks: () => [...adminKeys.all, 'parentStudentLinks'] as const,
}

// ============ Gradebook: the marking grid ============

export const gradebookKeys = {
  all: ['gradebook'] as const,
  // The same catalogue the admin table reads — see `catalogKeys`.
  courses: catalogKeys.courses,
  termCourses: catalogKeys.termCourses,
  enrollments: (termCourseId: string) =>
    [...gradebookKeys.all, 'enrollments', termCourseId] as const,
  students: (termCourseId: string) =>
    [...gradebookKeys.all, 'students', termCourseId] as const,
  /** Every cached grid, for a write that changes rosters it cannot enumerate. */
  rosters: () => [...gradebookKeys.all, 'students'] as const,
  assignments: (termCourseId: string) =>
    [...gradebookKeys.all, 'assignments', termCourseId] as const,
  assignment: (id: string) => [...gradebookKeys.all, 'assignment', id] as const,
  submission: (termCourseId: string) =>
    [...gradebookKeys.all, 'submission', termCourseId] as const,
  /**
   * Every course's submission banner. The approve/reject writes are addressed by
   * submission id and never learn which term-course it belongs to, so the banner
   * they flip can only be reached through the prefix.
   */
  submissions: () => [...gradebookKeys.all, 'submission'] as const,
  categories: (termCourseId: string) =>
    [...gradebookKeys.all, 'categories', termCourseId] as const,
  /** Every course's categories, for a write that re-weights them all at once. */
  allCategories: () => [...gradebookKeys.all, 'categories'] as const,
}

// ============ Course pages ============

export const courseDetailKeys = {
  all: ['courseDetail'] as const,
  detail: (id: string) => [...courseDetailKeys.all, id] as const,
  enrollments: (termCourseId: string) =>
    [...courseDetailKeys.all, 'enrollments', termCourseId] as const,
  eligibleStudents: (termCourseId: string) =>
    [...courseDetailKeys.all, 'eligible', termCourseId] as const,
}

export const calendarKeys = {
  all: ['calendar'] as const,
  upcoming: (params: { days?: number; studentId?: string; termCourseId?: string }) =>
    [...calendarKeys.all, 'upcoming', params] as const,
}

export const impersonationKeys = {
  all: ['impersonation'] as const,
  users: (role: string, search: string) =>
    [...impersonationKeys.all, 'users', role, search] as const,
}

export const settingsKeys = {
  all: ['settings'] as const,
  categories: (termCourseId: string) => [...settingsKeys.all, 'categories', termCourseId] as const,
  /** Every course's categories — see `gradebookKeys.allCategories`. */
  allCategories: () => [...settingsKeys.all, 'categories'] as const,
  gradeScales: (programType: string) => [...settingsKeys.all, 'gradeScales', programType] as const,
}

/**
 * The Settings dropdown's sections list — the third reader of the same endpoint,
 * now the same entry. Kept as a name so `cacheSync` and the hook that owns the
 * query still read the way they did.
 */
export const termCourseKeys = {
  all: catalogKeys.termCourses(),
  list: catalogKeys.termCourses,
}

export const gradeScaleKeys = {
  all: ['gradeScales'] as const,
  list: (programType: string) => [...gradeScaleKeys.all, 'list', programType] as const,
}

export const categoryTemplateKeys = {
  all: ['categoryTemplates'] as const,
  list: () => [...categoryTemplateKeys.all, 'list'] as const,
}

// ============ Student pages ============

export const studentDetailKeys = {
  all: ['studentDetail'] as const,
  detail: (id: string) => [...studentDetailKeys.all, id] as const,
  grades: (studentId: string) => [...studentDetailKeys.all, 'grades', studentId] as const,
  courseAssignments: (termCourseId: string) =>
    [...studentDetailKeys.all, 'assignments', termCourseId] as const,
}

export const studentRecordKeys = {
  health: (studentId: string) => ['studentHealth', studentId] as const,
  documents: (studentId: string) => ['studentDocuments', studentId] as const,
  intakeBatch: (batchId: string) => ['intakeBatch', batchId] as const,
  intakeBatches: () => ['intakeBatches'] as const,
}

export const studentCustomFieldKeys = {
  all: ['studentCustomFields'] as const,
  list: (includeArchived: boolean) =>
    [...studentCustomFieldKeys.all, { includeArchived }] as const,
}

export const yearEndGradeKeys = {
  all: ['yearEndGrades'] as const,
  forStudent: (studentId: string, academicYearId?: string) =>
    [...yearEndGradeKeys.all, studentId, academicYearId ?? 'all-years'] as const,
}

// ============ Submissions, grade posting, change requests ============

export const submissionKeys = {
  all: ['submissions'] as const,
  pending: () => [...submissionKeys.all, 'pending'] as const,
  byTerm: (termId: string) => [...submissionKeys.all, 'term', termId] as const,
  byTermCourse: (termCourseId: string) =>
    [...submissionKeys.all, 'termCourse', termCourseId] as const,
}

export const teacherSubmissionKeys = {
  all: ['teacherSubmissions'] as const,
  myPending: () => [...teacherSubmissionKeys.all, 'myPending'] as const,
  myAll: () => [...teacherSubmissionKeys.all, 'myAll'] as const,
}

export const gradePostingKeys = {
  all: ['gradePosting'] as const,
  coverage: (termId: string) => [...gradePostingKeys.all, 'coverage', termId] as const,
}

export const changeRequestKeys = {
  all: ['changeRequests'] as const,
  list: (status: ChangeRequestListStatus) =>
    [...changeRequestKeys.all, 'list', status] as const,
}

export const myGradeChangeKeys = {
  all: ['myGradeChanges'] as const,
  requests: () => [...myGradeChangeKeys.all, 'requests'] as const,
  notifications: () => [...myGradeChangeKeys.all, 'notifications'] as const,
}

export const postedGradeKeys = {
  all: ['postedGrades'] as const,
  byTermCourse: (termCourseId: string) =>
    [...postedGradeKeys.all, 'termCourse', termCourseId] as const,
}

export const auditKeys = {
  all: ['audit'] as const,
  list: (params: { entityType?: string; action?: string; limit: number; offset: number }) =>
    [...auditKeys.all, 'list', params] as const,
}

export const bulkEnrollmentKeys = {
  all: ['bulkEnrollment'] as const,
}

// ============ Attendance ============

export const attendanceKeys = {
  all: ['attendance'] as const,
  register: (homeroomId: string, date: string) =>
    [...attendanceKeys.all, 'register', homeroomId, date] as const,
  overview: (from: string, to: string) =>
    [...attendanceKeys.all, 'overview', from, to] as const,
  /** Every cached range. A write knows the day it touched, not the weeks on screen. */
  overviews: () => [...attendanceKeys.all, 'overview'] as const,
  /**
   * A report's totals. Under `attendance` like the overview, so every mark that
   * reaches the register reaches the reports summing it.
   */
  report: (scope: {
    from: string
    to: string
    homeroomId: string | null
    academicYearId: string | null
  }) => [...attendanceKeys.reports(), scope] as const,
  reports: () => [...attendanceKeys.all, 'report'] as const,
}

export const studentAttendanceKeys = {
  all: ['studentAttendance'] as const,
  history: (studentId: string, from: string | null, to: string | null) =>
    [...studentAttendanceKeys.all, 'history', studentId, from ?? '', to ?? ''] as const,
  pickups: (studentId: string) => [...studentAttendanceKeys.all, 'pickups', studentId] as const,
}

export const homeroomKeys = {
  all: ['homerooms'] as const,
  list: (academicYearId: string | null) =>
    [...homeroomKeys.all, 'list', academicYearId] as const,
}

// ============ Dashboards and portals ============

export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
}

export const studentGradesKeys = {
  all: ['studentGrades'] as const,
  myGrades: () => [...studentGradesKeys.all, 'me'] as const,
}

export const parentKeys = {
  all: ['parent'] as const,
  children: () => [...parentKeys.all, 'children'] as const,
  childGrades: (studentId: string) => [...parentKeys.all, 'childGrades', studentId] as const,
}

// ============ School-wide configuration ============

export const schoolSettingsKeys = {
  all: ['schoolSettings'] as const,
  current: () => [...schoolSettingsKeys.all, 'current'] as const,
}

export const gradeLevelKeys = {
  all: ['gradeLevels'] as const,
  list: () => [...gradeLevelKeys.all, 'list'] as const,
}

export const rolePermissionKeys = {
  all: ['rolePermissions'] as const,
  matrix: () => [...rolePermissionKeys.all, 'matrix'] as const,
}

export const fieldPermissionKeys = {
  all: ['fieldPermissions'] as const,
  rules: () => [...fieldPermissionKeys.all, 'rules'] as const,
  primaryAdmin: () => [...fieldPermissionKeys.all, 'primaryAdmin'] as const,
  admins: () => [...fieldPermissionKeys.all, 'admins'] as const,
}

// ============ The signed-in person's own account ============

/**
 * Not a school resource, and the only key here that names something this
 * database does not hold: the token behind the sign-in-and-security screen is
 * minted by WorkOS and reaches only the person the request came from. It has no
 * `cacheSync` entry for that reason — the writes it enables happen at the
 * provider, so there is nothing local for them to make stale.
 */
export const accountKeys = {
  all: ['account'] as const,
  securityToken: () => [...accountKeys.all, 'securityToken'] as const,
}
